package top.wcpe.allincore.ui.web.preview

import top.wcpe.allincore.ui.web.Container
import top.wcpe.allincore.ui.web.PixiRenderBridge
import top.wcpe.allincore.ui.web.Sprite
import top.wcpe.allincore.ui.web.WebRenderSurface
import top.wcpe.allincore.ui.web.WebTextureLease
import top.wcpe.allincore.ui.web.asset.ObjectUrlLease
import kotlin.js.Promise

/** 背景纹理 acquisition 与对象 URL 组成同一异步释放单元。 */
internal class PreviewBackgroundLoad(
    private val objectUrl: ObjectUrlLease,
    acquisition: Promise<WebTextureLease?>,
) {
    private lateinit var settleSignal: (Unit) -> Unit
    private lateinit var releaseSignal: (Unit) -> Unit
    private var textureLease: WebTextureLease? = null
    private var readyListener: (() -> Unit)? = null
    private var acquisitionSettled = false
    private var disposeRequested = false
    private var retryScheduled = false
    private var resourcesReleased = false

    val settled: Promise<Unit> = Promise { resolve, _ -> settleSignal = resolve }
    val releaseCompleted: Promise<Unit> = Promise { resolve, _ -> releaseSignal = resolve }
    val resourceUrl: String get() = objectUrl.url
    val texture: WebTextureLease? get() = textureLease

    init {
        acquisition.then { lease ->
            completeAcquisition(lease)
            Unit
        }.catch {
            completeAcquisition(null)
            Unit
        }
    }

    fun activate(onReady: () -> Unit) {
        if (disposeRequested || resourcesReleased) return
        readyListener = onReady
        if (acquisitionSettled && textureLease != null) onReady()
    }

    fun deactivate() {
        readyListener = null
    }

    fun dispose() {
        if (resourcesReleased) return
        disposeRequested = true
        readyListener = null
        if (acquisitionSettled && !retryScheduled) releaseOwnedResources()
    }

    private fun completeAcquisition(lease: WebTextureLease?) {
        if (acquisitionSettled) {
            lease?.release()
            return
        }
        acquisitionSettled = true
        textureLease = lease
        if (disposeRequested) {
            try {
                releaseOwnedResources()
            } finally {
                settleSignal(Unit)
            }
            return
        }
        settleSignal(Unit)
        if (lease != null) readyListener?.invoke()
    }

    private fun releaseOwnedResources() {
        if (resourcesReleased || !disposeRequested) return
        val lease = textureLease
        if (lease != null && !lease.release()) {
            scheduleReleaseRetry()
            return
        }
        textureLease = null
        val urlReleased = try {
            objectUrl.tryDispose()
        } catch (_: Throwable) {
            false
        }
        if (!urlReleased) {
            scheduleReleaseRetry()
            return
        }
        resourcesReleased = true
        releaseSignal(Unit)
    }

    private fun scheduleReleaseRetry() {
        if (retryScheduled || resourcesReleased) return
        retryScheduled = true
        scheduleBackgroundReleaseRetry {
            retryScheduled = false
            releaseOwnedResources()
        }
    }
}

/** HUD 模拟背景只切换受控 load，具体纹理与 URL 生命周期归 bundle 所有。 */
internal class PreviewBackground(
    private val surface: WebRenderSurface,
    private var width: Int,
    private var height: Int,
    private val textureLoader: (String) -> Promise<WebTextureLease?> = surface::acquireTexture,
) {
    private var current: PreviewBackgroundLoad? = null
    private var textureReady = false
    private var mode = "procedural"
    private var disposed = false

    internal fun currentMode(): String = mode

    fun resize(w: Int, h: Int) {
        width = w
        height = h
    }

    fun render(bridge: PixiRenderBridge) {
        val load = current
        if (mode == "image" && textureReady && load != null) {
            bridge.drawImage(load.resourceUrl, 0f, 0f, width.toFloat(), height.toFloat())
        } else {
            drawProcedural(bridge)
        }
    }

    fun render(stage: Container) {
        val texture = current?.texture?.texture
        if (mode == "image" && textureReady && texture != null) {
            val sprite = Sprite.from(texture)
            sprite.width = width.toDouble()
            sprite.height = height.toDouble()
            stage.addChild(sprite)
        } else {
            val bridge = PixiRenderBridge(stage, width.toFloat(), height.toFloat())
            drawProcedural(bridge)
        }
    }

    fun dispose() {
        if (disposed) return
        disposed = true
        current?.deactivate()
        current = null
        textureReady = false
        mode = "procedural"
    }

    internal fun prepare(background: PreviewCandidateBackground): PreviewBackgroundLoad {
        check(!disposed) { "背景绘制器已释放" }
        val acquisition = try {
            textureLoader(background.objectUrl.url)
        } catch (error: Throwable) {
            Promise.reject(error)
        }
        return PreviewBackgroundLoad(background.objectUrl, acquisition).also(background::attach)
    }

    internal fun replaceImage(load: PreviewBackgroundLoad?) {
        if (disposed) return
        current?.deactivate()
        current = load
        textureReady = false
        mode = "procedural"
        load?.activate {
            if (!disposed && current === load && load.texture != null) {
                textureReady = true
                mode = "image"
            }
        }
    }

    private fun drawProcedural(bridge: PixiRenderBridge) {
        val horizon = height * HORIZON_RATIO
        val bandHeight = horizon / SKY_COLORS.size
        SKY_COLORS.forEachIndexed { index, color ->
            bridge.fillRect(0f, index * bandHeight, width.toFloat(), bandHeight, color)
        }
        bridge.fillRect(0f, horizon, width.toFloat(), height - horizon, GROUND_COLOR)
        drawBlock(bridge, width * 0.12f, horizon - 46f, 40f, 46f)
        drawBlock(bridge, width * 0.40f, horizon - 70f, 54f, 70f)
        drawBlock(bridge, width * 0.72f, horizon - 34f, 34f, 34f)
    }

    private fun drawBlock(bridge: PixiRenderBridge, x: Float, y: Float, w: Float, h: Float) {
        bridge.fillRect(x, y, w, h, BLOCK_COLOR)
        bridge.drawLine(x, y, x + w, y, BORDER_WIDTH, BLOCK_BORDER_COLOR)
        bridge.drawLine(x + w, y, x + w, y + h, BORDER_WIDTH, BLOCK_BORDER_COLOR)
        bridge.drawLine(x + w, y + h, x, y + h, BORDER_WIDTH, BLOCK_BORDER_COLOR)
        bridge.drawLine(x, y + h, x, y, BORDER_WIDTH, BLOCK_BORDER_COLOR)
    }

    private companion object {
        const val HORIZON_RATIO = 0.62f
        const val GROUND_COLOR = 0xFF4A7A3A.toInt()
        const val BLOCK_COLOR = 0xFF3B5230.toInt()
        const val BLOCK_BORDER_COLOR = 0xFF223018.toInt()
        const val BORDER_WIDTH = 2f
        val SKY_COLORS = intArrayOf(
            0xFF2A4A7A.toInt(),
            0xFF3A5F92.toInt(),
            0xFF5A7FB0.toInt(),
            0xFF88A8CE.toInt(),
        )
    }
}

private fun scheduleBackgroundReleaseRetry(action: () -> Unit) {
    val global: dynamic = js("globalThis")
    global.setTimeout(action, BACKGROUND_RELEASE_RETRY_MS)
}

private const val BACKGROUND_RELEASE_RETRY_MS = 16
